import { Command } from 'cmdk';
import React, { useMemo, useCallback } from 'react';
import type { IconName } from '@customerio/pluma-icons';
import {
  TextField,
  Box,
  ModalPrimitiveDialog,
  ModalPrimitiveOverlay,
  ModalPrimitive,
  EmptyStateDescription,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Text,
  Flex,
  FormattedDateTime,
  EmptyStateGraphic,
  Spinner,
  KeyboardShortcut,
  InlineStack,
  type ModalPrimitiveProps,
  List,
  ListItem,
  Label,
} from '@customerio/pluma-components/react';
import { useIsFullScreenAssistantPage, useCopilotSessionContext } from '@/features/copilot';
import { open as openSidebar } from '@/features/sidebar';

import { servicesQuery } from '@/services/api';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useCurrentWorkspace } from '@/context/current-workspace';
import * as styles from './index.css';
import type { SearchResult } from '@/services/auth/types';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { createModal } from '@/hooks/system/create-modal';
import { LABEL } from './constants';
import { getIconNameFromSearchResult } from './utils';
import { useStoredHydraCommands } from './use-stored-hydra-commands';
import { useLocalSearchResults } from './use-local-search-results';
import { SEARCH_RESULT_HEADINGS } from './constants';
import { groupSearchResults } from './utils';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import {
  PlumaModal as PlumaModalCss,
  PlumaBreadcrumbs as PlumaBreadcrumbsCss,
} from '@customerio/pluma-components/css/components';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useQueryParamAction } from '@/hooks/use-query-param-action';
import { useMountEffect } from '@/hooks/use-mount-effect';

export const useCommandDialog = createModal('command-dialog', {
  type: 'ephemeral',
});

export function CommandDialog() {
  const isUniversalSearchEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchDialog);
  const isAiAssistantV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();
  const showAskAssistant = isAiAssistantV2 && !isFullScreenAssistantPage;
  const session = useCopilotSessionContext();
  const { track } = useAnalytics();

  const safeNavigate = useSafeNavigate();
  const commandDialog = useCommandDialog();
  const [inputValue, setInputValue] = React.useState('');
  const [debouncedInputValue] = useDebouncedValue(inputValue.trim(), 250);
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? 0;

  // Deep-link support: ?cmd-search=<query> opens the dialog with a prefilled query
  // Note: uses 'cmd-search' instead of 'search' to avoid collision with deliverability page's ?search= param
  useQueryParamAction('cmd-search', (value) => {
    void track('commandDialog', 'opened', { 'Entry Point': 'deep_link' });
    setInputValue(value);
    void commandDialog.show();
  });

  const modifierKeysPressed = React.useRef({ meta: false, ctrl: false });

  useMountEffect(() => {
    const resetModifiers = () => {
      modifierKeysPressed.current.meta = false;
      modifierKeysPressed.current.ctrl = false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey) modifierKeysPressed.current.meta = true;
      if (e.ctrlKey) modifierKeysPressed.current.ctrl = true;
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.metaKey) modifierKeysPressed.current.meta = false;
      if (!e.ctrlKey) modifierKeysPressed.current.ctrl = false;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        resetModifiers();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', resetModifiers);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', resetModifiers);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  });

  const handleInputFocus = useCallback((e: Event & { target: HTMLInputElement }) => {
    e.target.select();
  }, []);

  const { data, isLoading, isError } = servicesQuery.search.useQuery({
    queryKey: ['search', debouncedInputValue, workspaceId],
    queryData: {
      params: { workspaceId },
      query: { q: debouncedInputValue },
    },
    enabled: isUniversalSearchEnabled && commandDialog.isOpen && debouncedInputValue.length > 0 && workspaceId > 0,
    staleTime: 1000 * 30, // Consider search results stale after 30 seconds
    retryOnMount: false,
  });

  const closeDialog = useCallback(() => {
    commandDialog.close();
  }, [commandDialog]);

  // Toggle the menu when ⌘K is pressed
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (commandDialog.isOpen) {
          void track('commandDialog', 'closed', { Reason: 'shortcut' });
        } else {
          void track('commandDialog', 'opened', { 'Entry Point': 'shortcut' });
        }
        commandDialog.toggle();
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [commandDialog, track, closeDialog]);

  const groupedResults = useMemo(() => groupSearchResults(data?.body.results ?? []), [data]);

  // Using inputValue instead of debouncedInputValue as we aren't calling
  // an expensive API like /search and there probably won't be many commands to filter by
  const hydraCommands = useStoredHydraCommands(inputValue);

  // Local search results — handlers are called on-demand with the debounced query
  const localSearchGroups = useLocalSearchResults(debouncedInputValue);

  const handleItemSelect = useCallback(
    (url: string, kind: SearchResult['kind']) => {
      const openInNewTab = modifierKeysPressed.current.meta || modifierKeysPressed.current.ctrl;
      void track('commandDialog', 'itemSelected', {
        'Item Type': 'search_result',
        'Result Kind': kind,
        'Open in New Tab': openInNewTab,
      });
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        safeNavigate(url);
        closeDialog();
      }
    },
    [safeNavigate, closeDialog, track],
  );

  const handleLocalCommandSelect = useCallback(
    (onSelect: () => void | Promise<void>) => {
      void track('commandDialog', 'itemSelected', {
        'Item Type': 'local_command',
        'Result Kind': 'command',
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [closeDialog, track],
  );

  const handleLocalSearchSelect = useCallback(
    (onSelect: () => void | Promise<void>) => {
      void track('commandDialog', 'itemSelected', {
        'Item Type': 'local_search',
        'Result Kind': 'search',
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [closeDialog, track],
  );

  const handleAskAssistant = useCallback(() => {
    void track('commandDialog', 'itemSelected', {
      'Item Type': 'ask_assistant',
      'Result Kind': 'assistant',
    });
    openSidebar('copilot');
    if (debouncedInputValue.length > 0) {
      void session.sendMessage(debouncedInputValue).catch(sentryCaptureException);
    }
    closeDialog();
  }, [closeDialog, track, debouncedInputValue, session]);

  const handleDialogClose = useCallback<NonNullable<ModalPrimitiveProps['onClose']>>(
    (_event, reason) => {
      void track('commandDialog', 'closed', { Reason: reason ?? 'dismiss' });
      closeDialog();
    },
    [closeDialog, track],
  );

  const hasNoApiResults =
    data == null || (data != null && (data.body.results == null || data.body.results.length === 0));
  const hasLocalCommands = hydraCommands.length > 0;
  const hasLocalSearchResults = localSearchGroups.length > 0;
  const hasNoResults = hasNoApiResults && !hasLocalCommands && !hasLocalSearchResults;

  return (
    <ModalPrimitive
      initialFocusIndex={-1}
      animationTransitionDuration={125}
      isOpen={commandDialog.isOpen}
      onClose={handleDialogClose}
    >
      <ModalPrimitiveOverlay className={PlumaModalCss.classNames.modalOverlay}>
        <ModalPrimitiveDialog className={styles.dialog}>
          <Command loop label={LABEL} shouldFilter={false}>
            <Command.Input asChild value={inputValue} onValueChange={setInputValue}>
              <TextField
                autoFocus={true}
                placeholder={LABEL}
                onInput={(e) => setInputValue(e.target.value)}
                onFocus={handleInputFocus}
                ariaLabel="Use the command palette to search for campaigns, templates, segments, or anything else in this workspace."
                className={`${styles.largeTextField} fs-mask`}
                icon="search"
                iconColor="accent"
                size="sm"
                ml="200"
                mr={hasNoResults ? '200' : undefined}
                mt="100"
              />
            </Command.Input>
            <Command.List>
              {showAskAssistant && !(debouncedInputValue.length > 0 && !isLoading && (hasNoResults || isError)) ? (
                <Command.Group heading="Agent">
                  <Command.Item key="ask-assistant" value="ask-assistant" onSelect={handleAskAssistant} asChild>
                    <Box alignItems="flex-start" gap="050" display="flex">
                      <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                      <Flex direction="column">
                        <Text text="product-p">Ask Agent</Text>
                        <Text size="xxs" color="subtle">
                          {debouncedInputValue.length > 0 ? (
                            <>
                              Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI Agent
                            </>
                          ) : (
                            'Ask the AI Agent a question'
                          )}
                        </Text>
                      </Flex>
                    </Box>
                  </Command.Item>
                </Command.Group>
              ) : null}
              {hasLocalCommands ? (
                <Command.Group heading="Quick actions">
                  {hydraCommands.map((cmd) => (
                    <Command.Item
                      key={`local-${cmd.id}`}
                      value={`local-${cmd.id}`}
                      onSelect={() => handleLocalCommandSelect(cmd.onSelect)}
                      asChild
                    >
                      <Box alignItems="flex-start" gap="050" display="flex">
                        <Icon
                          mt="025"
                          size="sm"
                          sizeVersion="v2"
                          color="base"
                          name={(cmd.icon ?? 'command') as IconName}
                        />
                        <Flex direction="column">
                          <Text text="product-p">{cmd.label}</Text>
                          {cmd.description != null ? (
                            <Text size="xxs" color="subtle">
                              {cmd.description}
                            </Text>
                          ) : null}
                        </Flex>
                      </Box>
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
              {hasLocalSearchResults ? (
                <>
                  {localSearchGroups.map((group) => (
                    <Command.Group key={`search-${group.heading}`} heading={group.heading}>
                      {group.results.map((result) => (
                        <Command.Item
                          key={`search-${result.id}`}
                          value={`search-${result.id}`}
                          onSelect={() => handleLocalSearchSelect(result.onSelect)}
                          asChild
                        >
                          <Box alignItems="flex-start" gap="050" display="flex">
                            <Icon
                              mt="025"
                              size="sm"
                              sizeVersion="v2"
                              color="base"
                              name={(result.icon ?? 'search') as IconName}
                            />
                            <Flex direction="column">
                              <Text text="product-p">{result.label}</Text>
                              {result.description != null ? (
                                <Text size="xxs" color="subtle">
                                  {result.description}
                                </Text>
                              ) : null}
                            </Flex>
                          </Box>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ))}
                </>
              ) : null}
              {(hasLocalCommands || hasLocalSearchResults) && isLoading ? (
                <Command.Loading asChild>
                  <EmptyState>
                    <EmptyStateGraphic>
                      <Spinner className={styles.spinner} color="information" />
                    </EmptyStateGraphic>
                    <EmptyStateTitle>Searching in {currentWorkspace?.name ?? 'this workspace'}…</EmptyStateTitle>
                  </EmptyState>
                </Command.Loading>
              ) : null}
              {!hasLocalCommands && !hasLocalSearchResults && isLoading ? (
                <Command.Loading asChild>
                  <EmptyState>
                    <EmptyStateGraphic>
                      <Spinner className={styles.spinner} color="information" />
                    </EmptyStateGraphic>
                    <EmptyStateTitle>
                      Searching for "<span className="fs-mask">{debouncedInputValue}</span>"
                    </EmptyStateTitle>
                    <EmptyStateDescription>
                      Hang on while we search in {currentWorkspace?.name ?? 'this workspace'}
                    </EmptyStateDescription>
                  </EmptyState>
                </Command.Loading>
              ) : null}
              {debouncedInputValue.length === 0 && !hasLocalCommands && !hasLocalSearchResults ? (
                <Command.Empty asChild>
                  <EmptyState>
                    <EmptyStateTitle icon="search">Search {currentWorkspace?.name ?? 'Workspace'}</EmptyStateTitle>
                    <EmptyStateDescription>
                      Try searching for campaigns, templates, segments, or anything else in this workspace.
                    </EmptyStateDescription>
                  </EmptyState>
                </Command.Empty>
              ) : null}
              {debouncedInputValue.length > 0 && !isLoading && (hasNoResults || isError) ? (
                showAskAssistant ? (
                  <Command.Group heading="Agent">
                    <Command.Item key="ask-assistant" value="ask-assistant" onSelect={handleAskAssistant} asChild>
                      <Box alignItems="flex-start" gap="050" display="flex">
                        <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                        <Flex direction="column">
                          <Text text="product-p">No results found</Text>
                          <Text size="xxs" color="subtle">
                            Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI Agent
                          </Text>
                        </Flex>
                      </Box>
                    </Command.Item>
                  </Command.Group>
                ) : (
                  <Command.Empty asChild>
                    <EmptyState>
                      <EmptyStateTitle icon="search">
                        No results found for <span className="fs-mask">{debouncedInputValue}</span>
                      </EmptyStateTitle>
                      <EmptyStateDescription>Try adjusting your search to find matches.</EmptyStateDescription>
                    </EmptyState>
                  </Command.Empty>
                )
              ) : null}
              {data?.body.results != null ? (
                <>
                  {groupedResults.map(([kind, items]) => (
                    <Command.Group
                      key={kind}
                      heading={SEARCH_RESULT_HEADINGS?.[kind] ?? `Search Results for '${kind}'`}
                    >
                      {items.map((item, index) => {
                        const url = new URL(item.url);
                        const safeUrlValue = url.pathname + url.search;
                        const hasDescription = item.kind === 'campaign' || item.kind === 'segment';
                        const showBreadcrumbs =
                          item.kind === 'action' && isEmptyString(item.other.campaign_name) === false;
                        const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;

                        // TODO add logic on a per item basis to determine if the label should be shown
                        const hasLabel = false;

                        return (
                          <Command.Item
                            onSelect={() => handleItemSelect(safeUrlValue, item.kind)}
                            key={key}
                            value={key}
                            asChild
                          >
                            <Box alignItems="flex-start" gap="050" display="flex">
                              <Icon
                                mt="025"
                                name={getIconNameFromSearchResult(item)}
                                size="sm"
                                sizeVersion="v2"
                                color="base"
                              />
                              <Flex direction="column" gap="025">
                                <Flex direction="column">
                                  {hasLabel ? (
                                    <InlineStack alignBlock="center" gap="100">
                                      <Text text="product-p" className="fs-mask">
                                        {item.title}
                                      </Text>
                                      {/* TODO add logic on a per item basis to determine what label should be shown */}
                                      <Label>Test</Label>
                                    </InlineStack>
                                  ) : (
                                    <Text text="product-p" className="fs-mask">
                                      {item.title}
                                    </Text>
                                  )}
                                  {hasDescription && item.other?.description != null ? (
                                    <Text size="xxs" color="subtle" className="fs-mask">
                                      {item.other.description}
                                    </Text>
                                  ) : null}
                                </Flex>
                                {showBreadcrumbs ? (
                                  <List styleType="none" className={PlumaBreadcrumbsCss.classNames.breadcrumbsList}>
                                    <ListItem className={PlumaBreadcrumbsCss.classNames.breadcrumbsItem}>
                                      Campaigns
                                    </ListItem>
                                    <ListItem className={PlumaBreadcrumbsCss.classNames.breadcrumbsItem}>
                                      {item.other.campaign_name}
                                    </ListItem>
                                  </List>
                                ) : null}
                                {item.other?.created_at != null && item.other.created_at != null ? (
                                  <>
                                    <Text size="xxs" color="subtle">
                                      Created at{' '}
                                      <FormattedDateTime
                                        className="fs-mask"
                                        date={item.other.created_at}
                                        format="medium"
                                      />
                                    </Text>
                                  </>
                                ) : null}
                              </Flex>
                            </Box>
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  ))}
                </>
              ) : null}
            </Command.List>
          </Command>
          <InlineStack
            w="full"
            alignInline="space-between"
            py="100"
            px="200"
            alignBlock="center"
            className={styles.footer}
          >
            <Flex gap="050" alignItems="center">
              <KeyboardShortcut hotkey="Escape" />
              <Text size="xxs" color="subtle">
                Close
              </Text>
            </Flex>

            <InlineStack alignInline="space-between" gap="300">
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="ArrowUp" />
                <KeyboardShortcut hotkey="ArrowDown" />
                <Text size="xxs" color="subtle">
                  Navigate
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Enter" />
                <Text size="xxs" color="subtle">
                  Select
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Mod+Enter" />
                <Text size="xxs" color="subtle">
                  Open in new tab
                </Text>
              </Flex>
            </InlineStack>
          </InlineStack>
        </ModalPrimitiveDialog>
      </ModalPrimitiveOverlay>
    </ModalPrimitive>
  );
}
